import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ownerApi } from '../../lib/ownerApi';
import { ArrowLeft, RefreshCw, Mail, Building2, Crown, Activity, ShieldAlert, Send, Trash2, AlertTriangle, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const HEALTH_DOT = {
  healthy: 'bg-emerald-500',
  warning: 'bg-amber-500',
  critical: 'bg-rose-500',
  unknown: 'bg-stone-300 dark:bg-slate-600',
};

function Section({ title, icon: Icon, children, action, testId }) {
  return (
    <section className="relative rounded-2xl border border orka-border-subtle orka-surface-raised p-4" data-testid={testId}>
      <header className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-stone-500" />}
          {title}
        </h3>
        {action}
      </header>
      {children}
    </section>
  );
}

function KV({ k, v, mono = false }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 border-b border-stone-100 dark:border-slate-800 last:border-0">
      <span className="text-[11px] uppercase tracking-wider text-stone-500">{k}</span>
      <span className={`text-xs ${mono ? 'font-mono orka-text-secondary' : 'orka-text-primary'} truncate max-w-[60%]`}>
        {v == null || v === '' ? <span className="text-stone-400 italic">—</span> : String(v)}
      </span>
    </div>
  );
}

function ResetDialog({ account, onClose, onDone }) {
  const [level, setLevel] = useState('session');
  const [reason, setReason] = useState('');
  const [orgId, setOrgId] = useState(account?.organization_id || '');
  const [busy, setBusy] = useState(false);

  const requiresReason = level === 'user_account' || level === 'person_full';
  const requiresOrg = level === 'membership';

  const submit = async () => {
    if (requiresReason && !reason.trim()) {
      toast.error('Reason is required for high-risk resets');
      return;
    }
    setBusy(true);
    try {
      const body = {
        target_person_id: account.primary_person_id,
        target_user_id: account.primary_user_id,
        target_email: account.primary_email,
        level,
        reason: reason || undefined,
      };
      if (requiresOrg) body.org_id = orgId;
      await ownerApi.resetIdentity(body);
      toast.success(`Reset complete (${level})`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.message || 'Reset failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="orka-surface-raised rounded-2xl shadow-xl max-w-md w-full p-5" onClick={e => e.stopPropagation()} data-testid="owner-reset-dialog">
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert className="w-5 h-5 text-rose-500" />
          <h3 className="font-semibold">Reset Identity</h3>
        </div>
        <p className="text-xs text-stone-500 mb-4">Target: <span className="font-mono">{account.primary_email || account.account_id}</span></p>

        <div className="space-y-2 mb-4">
          {[
            { v: 'session', label: 'L1 — Revoke sessions only', risk: 'low' },
            { v: 'membership', label: 'L2 — Revoke membership', risk: 'medium' },
            { v: 'user_account', label: 'L3 — Reset user account', risk: 'high' },
            { v: 'person_full', label: 'L4 — Archive person', risk: 'critical' },
          ].map(o => (
            <label key={o.v} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${level === o.v ? 'border-violet-400 bg-violet-50 dark:bg-violet-950/30' : 'border orka-border-subtle hover:bg-stone-50 dark:hover:bg-slate-800'}`}>
              <input type="radio" name="reset-level" checked={level === o.v} onChange={() => setLevel(o.v)} data-testid={`owner-reset-level-${o.v}`} />
              <span className="flex-1">{o.label}</span>
              <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded-full ${
                o.risk === 'critical' ? 'bg-rose-100 text-rose-700' :
                o.risk === 'high' ? 'bg-amber-100 text-amber-700' :
                o.risk === 'medium' ? 'bg-cyan-100 text-cyan-700' :
                'bg-emerald-100 text-emerald-700'
              }`}>{o.risk}</span>
            </label>
          ))}
        </div>

        {requiresOrg && (
          <input
            type="text" placeholder="org_id" value={orgId}
            onChange={e => setOrgId(e.target.value)}
            className="w-full mb-3 px-3 h-9 rounded-lg border border orka-border-subtle bg-stone-50 dark:bg-slate-800 text-sm font-mono"
            data-testid="owner-reset-orgid"
          />
        )}

        <textarea
          placeholder={requiresReason ? 'Reason (required for high-risk resets)' : 'Reason (optional)'}
          value={reason}
          onChange={e => setReason(e.target.value)}
          className="w-full mb-4 p-2 rounded-lg border border orka-border-subtle bg-stone-50 dark:bg-slate-800 text-sm h-20 resize-none"
          data-testid="owner-reset-reason"
        />

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 h-9 rounded-full border border orka-border-subtle text-sm" data-testid="owner-reset-cancel">Cancel</button>
          <button
            onClick={submit}
            disabled={busy || (requiresReason && !reason.trim()) || (requiresOrg && !orgId)}
            className="px-4 h-9 rounded-full bg-rose-600 text-white text-sm disabled:opacity-50"
            data-testid="owner-reset-submit"
          >
            {busy ? 'Resetting…' : 'Confirm reset'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OwnerAccountDetailPage() {
  const { account_id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReset, setShowReset] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = () => {
    setLoading(true);
    ownerApi.getAccount(account_id)
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { toast.error(e.message); setLoading(false); });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [account_id]);

  const recomputeHealth = async () => {
    setRefreshing(true);
    try {
      await ownerApi.recomputeHealth(account_id);
      toast.success('Health recomputed');
      load();
    } catch (e) {
      toast.error(e.message);
    } finally { setRefreshing(false); }
  };

  if (loading || !data) {
    return <div className="text-stone-500 text-sm" data-testid="owner-account-loading">Loading…</div>;
  }

  const a = data.account;
  const sub = data.subscription;

  return (
    <div className="space-y-4" data-testid="owner-account-detail">
      <Link to="/owner/accounts" className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-700">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Accounts
      </Link>

      {/* Header card */}
      <div className="rounded-2xl border border orka-border-subtle orka-surface-raised p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-xl font-semibold">{a.display_name}</h1>
              <span className="text-[10px] uppercase px-1.5 py-0.5 rounded-full orka-surface-subtle orka-text-secondary capitalize">{a.account_type}</span>
            </div>
            <div className="text-xs text-stone-500 font-mono">{a.account_id}</div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="text-xs orka-text-secondary">{a.primary_email || '—'}</span>
              <span className="inline-flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${HEALTH_DOT[a.health?.status] || HEALTH_DOT.unknown}`} />
                <span className="text-[11px] capitalize">{a.health?.status || 'unknown'}</span>
              </span>
            </div>
            {a.health?.reasons?.length > 0 && (
              <ul className="mt-2 space-y-1" data-testid="owner-account-health-reasons">
                {a.health.reasons.map((r, i) => (
                  <li key={i} className="text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
                    <AlertTriangle className="w-3 h-3" /> {r}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <button
              onClick={recomputeHealth}
              disabled={refreshing}
              className="px-3 h-9 rounded-full border border orka-border-subtle text-xs flex items-center gap-1.5 hover:bg-stone-50 dark:hover:bg-slate-800"
              data-testid="owner-account-recompute-health"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Recompute health
            </button>
            <button
              onClick={() => setShowReset(true)}
              className="px-3 h-9 rounded-full bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-900 text-xs flex items-center gap-1.5"
              data-testid="owner-account-reset"
            >
              <KeyRound className="w-3.5 h-3.5" />
              Reset identity
            </button>
          </div>
        </div>
      </div>

      {/* Two-column data */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section title="Commercial" icon={Crown} testId="owner-section-commercial"
          action={<CommercialEditor account={a} onSaved={load} />}
        >
          <KV k="Plan" v={a.plan_key} mono />
          <KV k="Status" v={a.commercial_status} />
          <KV k="Lifecycle" v={a.lifecycle_stage} />
          <KV k="Subscription" v={a.subscription_id} mono />
          <KV k="Stripe customer" v={data.billing_customer?.stripe_customer_id} mono />
          <KV k="Sub status" v={sub?.status} />
          <KV k="Period end" v={sub?.current_period_end?.slice(0, 10)} />
          <KV k="MRR contribution" v={sub ? `$${(sub.amount_total || 0) / 100}/${sub.currency}` : '—'} />
        </Section>

        <Section title="Identity" icon={Mail} testId="owner-section-identity">
          <KV k="Primary email" v={a.primary_email} />
          <KV k="Person ID" v={a.primary_person_id} mono />
          <KV k="User ID" v={a.primary_user_id} mono />
          <KV k="Sessions (recent)" v={data.sessions?.length ?? 0} />
          <KV k="Memberships" v={data.memberships?.length ?? 0} />
          <KV k="Pending invites" v={(data.invitations || []).filter(i => i.status === 'pending').length} />
        </Section>

        <Section title="Organization & workspaces" icon={Building2} testId="owner-section-org">
          <KV k="Organization" v={data.organization?.name || a.organization_id || '—'} />
          <KV k="Workspaces" v={(data.workspaces || []).length} />
          <KV k="Module installations" v={(data.module_installations || []).length} />
        </Section>

        <Section title="Demo" icon={Activity} testId="owner-section-demo">
          <KV k="Is demo" v={a.demo?.is_demo ? 'yes' : 'no'} />
          <KV k="Template" v={a.demo?.demo_template_key} />
          <KV k="Expires" v={a.demo?.demo_expires_at?.slice(0, 10)} />
          <KV k="Converted at" v={a.demo?.converted_to_real_account_at?.slice(0, 10)} />
        </Section>
      </div>

      {/* Memberships + Invites + Events */}
      <Section title="Memberships" icon={Building2} testId="owner-section-memberships">
        {(data.memberships || []).length === 0 ? (
          <div className="text-xs text-stone-400 italic">No memberships.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wider text-stone-500">
              <tr><th className="text-left py-1.5">Org</th><th className="text-left">Role</th><th className="text-left">Status</th><th className="text-left">Workspace</th></tr>
            </thead>
            <tbody>
              {data.memberships.map(m => (
                <tr key={m.membership_id} className="border-t border-stone-100 dark:border-slate-800">
                  <td className="py-1.5 font-mono">{m.org_id || '—'}</td>
                  <td>{m.role}</td>
                  <td><span className={`px-1.5 py-0.5 rounded text-[10px] ${m.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-600'}`}>{m.status}</span></td>
                  <td className="font-mono">{m.workspace_id || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title="Invitations" icon={Send} testId="owner-section-invitations">
        {(data.invitations || []).length === 0 ? (
          <div className="text-xs text-stone-400 italic">No invitations.</div>
        ) : (
          <ul className="space-y-1">
            {data.invitations.slice(0, 10).map(i => (
              <li key={i.invitation_id || i.token} className="text-xs flex items-center gap-2 border-b border-stone-100 dark:border-slate-800 py-1.5 last:border-0">
                <span className="font-mono text-stone-500">{i.token?.slice(0, 8) || '—'}</span>
                <span className="flex-1">{i.email}</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                  i.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                  i.status === 'accepted' ? 'bg-emerald-100 text-emerald-700' :
                  'bg-stone-100 text-stone-600'
                }`}>{i.status}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Owner action history" icon={Activity} testId="owner-section-events">
        {(data.events || []).length === 0 ? (
          <div className="text-xs text-stone-400 italic">No owner actions on this account yet.</div>
        ) : (
          <ul className="space-y-1.5">
            {data.events.map(e => (
              <li key={e.event_id} className="text-xs flex items-start gap-2 border-b border-stone-100 dark:border-slate-800 py-1.5 last:border-0">
                <span className="font-mono text-stone-400 shrink-0">{(e.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                <span className="font-medium">{e.event_type}</span>
                <span className="text-stone-500">{e.actor_email}</span>
                {e.reason && <span className="text-stone-600 italic">— {e.reason}</span>}
                <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded-full ${
                  e.risk_level === 'critical' ? 'bg-rose-100 text-rose-700' :
                  e.risk_level === 'high' ? 'bg-amber-100 text-amber-700' : 'bg-stone-100 text-stone-600'
                }`}>{e.risk_level}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {showReset && (
        <ResetDialog
          account={a}
          onClose={() => setShowReset(false)}
          onDone={load}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// CommercialEditor — inline edit of account_type / commercial_status /
// plan_key. Surfaces the "Testing Account" type/tier for owner-waived billing.
// ------------------------------------------------------------------
const ACCOUNT_TYPES = [
  { value: 'personal',         label: 'Personal' },
  { value: 'team',             label: 'Team' },
  { value: 'business',         label: 'Business' },
  { value: 'enterprise',       label: 'Enterprise' },
  { value: 'trial',            label: 'Trial' },
  { value: 'demo',             label: 'Demo' },
  { value: 'internal_test',    label: 'Internal test' },
  { value: 'testing_account',  label: 'Testing account (free · waived billing)' },
];
const COMMERCIAL_STATUSES = [
  { value: 'free',        label: 'Free' },
  { value: 'trial',       label: 'Trial' },
  { value: 'demo',        label: 'Demo' },
  { value: 'active_paid', label: 'Active paid' },
  { value: 'past_due',    label: 'Past due' },
  { value: 'cancelled',   label: 'Cancelled' },
  { value: 'expired',     label: 'Expired' },
  { value: 'internal',    label: 'Internal' },
  { value: 'testing',     label: 'Testing (owner-waived)' },
];
const PLAN_KEYS = [
  { value: '',           label: '—' },
  { value: 'free',       label: 'Free' },
  { value: 'starter',    label: 'Starter' },
  { value: 'pro',        label: 'Pro' },
  { value: 'team',       label: 'Team' },
  { value: 'business',   label: 'Business' },
  { value: 'enterprise', label: 'Enterprise' },
];

function CommercialEditor({ account, onSaved }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState(account.account_type || 'personal');
  const [status, setStatus] = useState(account.commercial_status || 'free');
  const [plan, setPlan] = useState(account.plan_key || '');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const dirty =
    type !== account.account_type ||
    status !== account.commercial_status ||
    (plan || '') !== (account.plan_key || '');

  const applyTestingPreset = () => {
    setType('testing_account');
    setStatus('testing');
    setPlan('enterprise');
    if (!notes) setNotes('Owner-assigned testing account (free, waived billing).');
  };

  const save = async () => {
    setBusy(true);
    try {
      await ownerApi.patchAccount(account.account_id, {
        account_type: type,
        commercial_status: status,
        plan_key: plan || null,
        notes: notes.trim() || undefined,
      });
      toast.success('Account updated');
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.message || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="text-[11px] uppercase tracking-wider font-semibold orka-text-intent hover:underline orka-focus-ring rounded-sm"
        data-testid="owner-commercial-edit"
      >
        Edit
      </button>
    );
  }

  return (
    <div className="absolute right-4 top-4 z-10 w-[320px] rounded-2xl border orka-border-default orka-surface-raised shadow-xl p-4" data-testid="owner-commercial-editor">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold orka-text-primary">Edit commercial</h4>
        <button onClick={() => setOpen(false)} className="text-xs orka-text-tertiary" data-testid="owner-commercial-editor-cancel">Cancel</button>
      </div>
      <div className="flex flex-col gap-2.5">
        <Field label="Account type">
          <select value={type} onChange={(e) => setType(e.target.value)} className="w-full h-9 px-2 rounded-lg border orka-border-default orka-surface-base orka-text-primary text-xs" data-testid="owner-commercial-editor-type">
            {ACCOUNT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="Commercial status">
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full h-9 px-2 rounded-lg border orka-border-default orka-surface-base orka-text-primary text-xs" data-testid="owner-commercial-editor-status">
            {COMMERCIAL_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </Field>
        <Field label="Plan key">
          <select value={plan} onChange={(e) => setPlan(e.target.value)} className="w-full h-9 px-2 rounded-lg border orka-border-default orka-surface-base orka-text-primary text-xs" data-testid="owner-commercial-editor-plan">
            {PLAN_KEYS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </Field>
        <Field label="Audit note (optional)">
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Reason for change"
            className="w-full h-9 px-2 rounded-lg border orka-border-default orka-surface-base orka-text-primary text-xs"
            data-testid="owner-commercial-editor-notes"
          />
        </Field>
        <button
          onClick={applyTestingPreset}
          type="button"
          className="h-9 mt-1 rounded-full border border-amber-300 text-amber-700 dark:text-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 text-xs font-semibold hover:bg-amber-100 dark:hover:bg-amber-900/40 orka-transition-colors"
          data-testid="owner-commercial-editor-testing-preset"
        >
          Mark as Testing Account (free)
        </button>
        <button
          onClick={save}
          disabled={!dirty || busy}
          className="h-9 rounded-lg bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs font-semibold disabled:opacity-50"
          data-testid="owner-commercial-editor-save"
        >
          {busy ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wider orka-text-tertiary font-semibold">{label}</span>
      {children}
    </label>
  );
}
